import React, {useEffect, useRef} from 'react';
import { PrimaryButton, Image, IconButton, TextField, Icon, useTheme} from '@fluentui/react';
import {IProductBundleRef} from "../../models/ProductBundles";
import BundleCardExtras from "./BundleCardExtras";
import {
    useAddProductBundleToWorkspaceMutation,
    useGetProductBundleRefsQuery,
    usePostProductBundleMutation
} from "../../store/endpoints/ProductBundleEndpoints";
import StorefrontBundleModal from "./StorefrontBundleModal";
import {BUNDLE_ICON, PLACEHOLDER_IMAGE} from "../../Constants";

export const BUNDLE_DRAG_TYPE = 'application/x-bundle-ids';

interface IProductBundleWorkspaceTableProps {
    showResults?: boolean;
    onLoading?: (loading: boolean) => void;
    onResultsCountChange?: (count: number) => void;
    onSelectedBundleChange?: (bundle: IProductBundleRef | undefined) => void;
    /** Opens a bundle's details (the peek's "Open bundle"); selecting a card is a different action. */
    onOpenBundle?: (bundleId: string) => void;
    onHide?: () => void;
    workspaceId: string;
}

const ProductBundleWorkspaceTable: React.FC<IProductBundleWorkspaceTableProps> = (props) => {
    const theme = useTheme();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedBundle, setSelectedBundle] = React.useState<IProductBundleRef | undefined>();
    const [selectedBundleIds, setSelectedBundleIds] = React.useState<Set<string>>(new Set());
    const [detailsBundle, setDetailsBundle] = React.useState<IProductBundleRef | undefined>();
    const [canScrollLeft, setCanScrollLeft] = React.useState(false);
    const [canScrollRight, setCanScrollRight] = React.useState(false);
    const [leftHover, setLeftHover] = React.useState(false);
    const [rightHover, setRightHover] = React.useState(false);
    const [keyword, setKeyword] = React.useState<string>('');
    const [debouncedKeyword, setDebouncedKeyword] = React.useState<string>('');

    const handleBundleClick = (item: IProductBundleRef, e: React.MouseEvent) => {
        const id = item.id ?? '';
        if (e.ctrlKey || e.metaKey) {
            setSelectedBundleIds(prev => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            });
        } else {
            setSelectedBundleIds(new Set([id]));
            setSelectedBundle(item);
            props.onSelectedBundleChange?.(item);
        }
    };

    const handleDragStart = (item: IProductBundleRef, e: React.DragEvent) => {
        const id = item.id ?? '';
        // If the dragged item isn't already in the selection, select only it
        const ids = selectedBundleIds.has(id)
            ? Array.from(selectedBundleIds)
            : [id];
        e.dataTransfer.setData(BUNDLE_DRAG_TYPE, JSON.stringify(ids));
        e.dataTransfer.effectAllowed = 'copy';
    };
    
    const retrieveBundle = useGetProductBundleRefsQuery({keywords: debouncedKeyword});
    const [addToWorkspace] = useAddProductBundleToWorkspaceMutation()
    
    React.useEffect(() => {
        const handler = setTimeout(() => setDebouncedKeyword(keyword), 500);
        return () => clearTimeout(handler);
    }, [keyword]);
    
    useEffect(() => {
        if (retrieveBundle.isLoading || retrieveBundle.isFetching){
            setLoading(true);
            props.onLoading?.(true);
        }
        else {
            if(retrieveBundle.error){
                setError('Error retrieving product bundles');
                setLoading(false);
                props.onLoading?.(false);
                props.onResultsCountChange?.(0);
            }
            else if(retrieveBundle.data){
                setLoading(false);
                props.onLoading?.(false);
                props.onResultsCountChange?.(retrieveBundle.data.items.length);
            }
        }
    }, [retrieveBundle.isLoading, retrieveBundle.isFetching]);

    useEffect(() => {
        checkScroll();
        const handleResize = () => checkScroll();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [retrieveBundle.data]);
    

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const cardWidth = 200; // minWidth of card
    const gap = 54; // gap between cards

    const checkScroll = () => {
        const el = scrollContainerRef.current;
        if (el) {
            setCanScrollLeft(el.scrollLeft > 0);
            setCanScrollRight(el.scrollLeft + el.clientWidth + 1 < el.scrollWidth);
        }
    };

    const handleScroll = () => checkScroll();


    const scrollByCard = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = cardWidth + gap;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div>
            {props.showResults &&
                <div style={{
                    position: 'relative',
                    background: '#f5f6fa',
                    borderRadius: 8,
                    padding: 24,
                    marginBottom: 24
                }}>
                <div style={{display: 'flex'}} >
                    <IconButton
                        iconProps={{ iconName: 'Cancel' }}
                        ariaLabel="Close bundles"
                        title="Close"
                        style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 2,
                            background: 'transparent'
                        }}
                        onClick={() => props.onHide?.()}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginLeft: 40, marginBottom: 16 }}>
                        <h3 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <Icon iconName={BUNDLE_ICON} aria-hidden styles={{ root: { fontSize: 18, color: theme.palette.themePrimary } }} />
                            Bundles
                        </h3>
                        <TextField
                            placeholder="Search bundles..."
                            value={keyword}
                            onChange={(_, v) => setKeyword(v || '')}
                            styles={{ root: { width: 250 } }}
                        />
                    </div>
                </div>
    
                <div style={{position: 'relative', width: '100%' }}>
                    {/* Left Arrow */}
                    {canScrollLeft && 
                    <IconButton
                        iconProps={{ iconName: 'ChevronLeft' }}
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 50,
                            height: 280,
                            zIndex: 1,
                            background: leftHover ? 'rgb(120, 156, 47, 0.75)' : 'rgba(255,255,255,0.7)'
                        }}
                        onClick={() => scrollByCard('left')}
                        ariaLabel="Scroll left"
                        onMouseEnter={() => setLeftHover(true)}
                        onMouseLeave={() => setLeftHover(false)}
                    />
                    }
                    {/* Scrollable Container */}
        
                    <div
                        ref={scrollContainerRef}
                        onScroll={handleScroll}
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            // Grows with the tallest card (the cost line can take two rows) so the Details button never spills out.
                            minHeight: '300px',
                            justifyContent: 'flex-start',
                            overflowX: 'auto',
                            width: '100%',
                            gap: '10px',
                            scrollBehavior: 'smooth'
                        }}
                    >
        
                        {retrieveBundle.data?.items.map((item) => {
                            const isSelected = selectedBundleIds.has(item.id ?? '') || selectedBundle?.id === item.id;
                            const isMultiSelected = selectedBundleIds.size > 1 && selectedBundleIds.has(item.id ?? '');

                            return(
                            <div 
                                key={item.id} 
                                draggable
                                onDragStart={(e) => handleDragStart(item, e)}
                                style={{
                                    backgroundColor: isSelected ? 'rgb(220, 237, 200)' : '#fff',
                                    border: isSelected
                                        ? `4px solid ${isMultiSelected ? 'rgb(60, 120, 200)' : 'rgb(120, 156, 47)'}`
                                        : '1px solid #ccc',
                                    padding: '10px', 
                                    margin: '10px',
                                    cursor: 'grab',
                                    minWidth: '200px',
                                    maxWidth: '200px',
                                    userSelect: 'none',
                                }}
                                onClick={(e) => handleBundleClick(item, e)}
                            >
                                <div style={{
                                    height: '150px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {item.imageUrl && item.imageUrl != '' ?
                                    <Image src={item.imageUrl} alt={item.name}
                                                             style={{width: '80%', height: 'auto', maxHeight: '185px', margin: '0 auto'}}/> :
                                    <Image src={PLACEHOLDER_IMAGE} alt={item.name}
                                           style={{width: '80%', height: 'auto', margin: '0 auto'}}/>
                                    }
                                </div>
                                <div style={{height: '40px', width: '90%', display: 'flex', alignItems: 'center',}}>
                                    <h3 style={{
                                        textAlign: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}>{item.name}</h3>
                                </div>
                                {item.id && <BundleCardExtras bundleId={item.id} name={item.name} onOpen={props.onOpenBundle ? () => props.onOpenBundle?.(item.id ?? '') : undefined} />}
                                <PrimaryButton
                                    text="Add to Workspace"
                                    iconProps={{iconName: 'Add'}}
                                    style={{width: '100%'}}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if(item.id) {
                                            addToWorkspace({
                                                bundleId: item.id,
                                                workspaceId: props.workspaceId
                                            }).unwrap();
                                        }
                                    }}
                                />
                            </div>);
                        })}
                    </div>
                    {/* Right Arrow */}
                    {canScrollRight && 
                    <IconButton
                        iconProps={{ iconName: 'ChevronRight' }}
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 50,
                            height: 280,
                            zIndex: 1,
                            background: rightHover ? 'rgb(120, 156, 47, 0.75)' : 'rgba(255,255,255,0.7)'
                        }}
                        onClick={() => scrollByCard('right')}
                        ariaLabel="Scroll right"
                        onMouseEnter={() => setRightHover(true)}
                        onMouseLeave={() => setRightHover(false)}
                    />
                    }
                </div>
                {detailsBundle?.id &&
                    <StorefrontBundleModal
                        bundleId={detailsBundle.id}
                        isOpen={true}
                        onDismiss={() => setDetailsBundle(undefined)}
                    />
                }
            </div>}
        </div>
    );
}

export default ProductBundleWorkspaceTable;